import { dislikedJobStorage } from "./dislikedJobsStorage";
import {
  dispatchDislikedJobStateChanged,
  subscribeDislikedJobStateChanged,
} from "../../shared/native-modules/tare/dislikedJobStateChangedEvent";
import { dislikeJob as callDislikeJobAPI } from "../native-module/dislikeJob";

import React, { createContext, useContext, useState, type ReactNode, useEffect } from "react";
import { Platform } from "react-native";

interface DislikedJobsContextType {
  dislikedJobs: Set<string>;
  dislikeJob: (jobKey: string, dislikeJobPageId: string, tk: string | undefined) => Promise<void>;
  undoDislikeJob: (
    jobKey: string,
    dislikeJobPageId: string,
    tk: string | undefined,
  ) => Promise<void>;
}

const DislikedJobsContext = createContext<DislikedJobsContextType>({
  dislikedJobs: new Set(),
  dislikeJob: async () => {},
  undoDislikeJob: async () => {},
});

export const DislikedJobsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dislikedJobs, setDislikedJobs] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    dislikedJobStorage
      .getDislikedJobs()
      .then((jobKeys) => {
        if (!cancelled) {
          setDislikedJobs(new Set(jobKeys));
        }
      })
      .catch((e) => console.error("Failed to load disliked jobs:", e));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    const subscription = subscribeDislikedJobStateChanged((event) => {
      if (event.source === "jobcard") {
        return;
      }

      setDislikedJobs((prev) => {
        const next = new Set(prev);
        if (event.isDisliked) {
          next.add(event.jobKey);
        } else {
          next.delete(event.jobKey);
        }
        return next;
      });
    });

    return () => {
      subscription.remove();
    };
  }, []);

  const dislikeJob = async (jobKey: string, dislikeJobPageId: string, tk?: string) => {
    setDislikedJobs((prev) => {
      const newSet = new Set(prev);
      newSet.add(jobKey);
      return newSet;
    });

    // Invoke API
    await callDislikeJobAPI(jobKey, dislikeJobPageId, false, tk).catch((e) => console.error(e));
    // Data Persistence
    await dislikedJobStorage.addDislikedJob(jobKey).catch((e) => console.error(e));

    if (Platform.OS === "web") {
      dispatchDislikedJobStateChanged({
        eventType: "DislikedJobStateChanged",
        jobKey,
        isDisliked: true,
        source: "jobcard",
        ...(tk ? { vjtk: tk } : {}),
      });
    }
  };

  const undoDislikeJob = async (jobKey: string, dislikeJobPageId: string, tk?: string) => {
    setDislikedJobs((prev) => {
      const newSet = new Set(prev);
      newSet.delete(jobKey);
      return newSet;
    });

    // Invoke API
    await callDislikeJobAPI(jobKey, dislikeJobPageId, true, tk).catch((e) => console.error(e));
    // Data Persistence
    await dislikedJobStorage.removeDislikedJob(jobKey).catch((e) => console.error(e));

    if (Platform.OS === "web") {
      dispatchDislikedJobStateChanged({
        eventType: "DislikedJobStateChanged",
        jobKey,
        isDisliked: false,
        source: "jobcard",
        ...(tk ? { vjtk: tk } : {}),
      });
    }
  };

  const contextValue: DislikedJobsContextType = {
    dislikedJobs,
    dislikeJob,
    undoDislikeJob,
  };

  return (
    <DislikedJobsContext.Provider value={contextValue}>{children}</DislikedJobsContext.Provider>
  );
};

export const useDislikedJobsContext = () => {
  return useContext(DislikedJobsContext);
};
